/** Client-side heuristic for whether a tenant-scoped batch value spans tenants.
 *
 *  This ONLY decides whether to prompt the operator for a cross-tenant strategy
 *  up front — the server re-detects the condition authoritatively and rejects a
 *  missing/unsupported strategy with a 400. We keep the heuristic cheap and
 *  fail-safe: when unsure, we still submit and let the server's 400 drive the
 *  strategy prompt.
 *
 *  Rules:
 *   - A system value (`valueTenantId == null`) is usable everywhere → never
 *     cross-tenant.
 *   - ids-mode with the rows loaded → cross-tenant if any selected row's tenant
 *     differs from the value's tenant.
 *   - all_matching → cross-tenant unless the grid is filtered to exactly the
 *     value's tenant (then every matched row is in-tenant).
 */
import type { BatchSelection } from '@/api/batch';

export function detectCrossTenant(
  valueTenantId: number | null,
  selection: BatchSelection,
  opts: { rows?: Array<{ tenantId: number | null }>; gridTenantFilter?: number | null },
): boolean {
  // A system-level value is usable in every tenant.
  if (valueTenantId == null) return false;

  if (selection.mode === 'ids') {
    const rows = opts.rows;
    if (!rows || rows.length === 0) return false; // can't tell → let the server decide
    return rows.some((r) => r.tenantId !== valueTenantId);
  }

  // all_matching: in-tenant only when the grid is scoped to the value's tenant.
  return opts.gridTenantFilter !== valueTenantId;
}
